import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Helper function to get auth token from sessionStorage
const getAuthToken = () => {
  const storeData = sessionStorage.getItem('storekhadamat');
  const pharmacyData = sessionStorage.getItem('pharmacykhadamat');
  
  if (storeData) {
    try {
      const parsed = JSON.parse(storeData);
      return parsed.token;
    } catch {
      return null;
    }
  }
  
  if (pharmacyData) {
    try {
      const parsed = JSON.parse(pharmacyData);
      return parsed.token;
    } catch {
      return null;
    }
  }
  
  return null;
};

const initialState = {
  promoCodes: [],
  promoCode: null,
  pagination: {
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  },
  loading: false,
  error: null,
  success: null
};

// Get all promo codes with filters
export const fetchPromoCodes = createAsyncThunk(
  'promoCodes/fetchPromoCodes',
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        page = 1,
        from = '',
        to = '',
        code = '',
        status = ''
      } = params;

      const token = getAuthToken();
      if (!token) {
        return rejectWithValue('لا يوجد token للمصادقة');
      }

      const queryParams = new URLSearchParams();
      if (page) queryParams.append('page', page);
      if (from) queryParams.append('from', from);
      if (to) queryParams.append('to', to);
      if (code) queryParams.append('code', code);
      if (status) queryParams.append('status', status);

      const response = await axios.get(`${BASE_URL}/promo-codes?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في جلب بيانات البرومو كود');
    }
  }
);

// Get single promo code details
export const fetchPromoCodeDetails = createAsyncThunk(
  'promoCodes/fetchPromoCodeDetails',
  async (promoCodeId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue('لا يوجد token للمصادقة');
      }

      const response = await axios.get(`${BASE_URL}/promo-codes/${promoCodeId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في جلب تفاصيل البرومو كود');
    }
  }
);

// Create new promo code
export const createPromoCode = createAsyncThunk(
  'promoCodes/createPromoCode',
  async (promoCodeData, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue('لا يوجد token للمصادقة');
      }

      const response = await axios.post(`${BASE_URL}/promo-codes`, promoCodeData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في إنشاء البرومو كود');
    }
  }
);

// Update promo code
export const updatePromoCode = createAsyncThunk(
  'promoCodes/updatePromoCode',
  async ({ promoCodeId, promoCodeData }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue('لا يوجد token للمصادقة');
      }

      const response = await axios.post(`${BASE_URL}/promo-codes/${promoCodeId}`, promoCodeData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في تحديث البرومو كود');
    }
  }
);

// Delete promo code
export const deletePromoCode = createAsyncThunk(
  'promoCodes/deletePromoCode',
  async (promoCodeId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue('لا يوجد token للمصادقة');
      }

      await axios.delete(`${BASE_URL}/promo-codes/${promoCodeId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': 'ar',
          'lang': 'ar',
        },
      });
      return promoCodeId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في حذف البرومو كود');
    }
  }
);

const promoCodesSlice = createSlice({
  name: 'promoCodes',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    clearPromoCode: (state) => {
      state.promoCode = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch promo codes
      .addCase(fetchPromoCodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPromoCodes.fulfilled, (state, action) => {
        state.loading = false;
        // التعامل مع الـ response structure الصحيح
        const data = action.payload.data || action.payload;
        state.promoCodes = Array.isArray(data.promo_codes) ? data.promo_codes : [];
        state.pagination = data.pagination || state.pagination;
      })
      .addCase(fetchPromoCodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch promo code details
      .addCase(fetchPromoCodeDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPromoCodeDetails.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload.data || action.payload;
        state.promoCode = data;
      })
      .addCase(fetchPromoCodeDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create promo code
      .addCase(createPromoCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPromoCode.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || 'تم إنشاء البرومو كود بنجاح';
        const data = action.payload.data || action.payload;
        const promoCodeData = data.promo_code || data;
        if (promoCodeData) {
          state.promoCodes.unshift(promoCodeData);
        }
      })
      .addCase(createPromoCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update promo code
      .addCase(updatePromoCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePromoCode.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || 'تم تحديث البرومو كود بنجاح';
        const data = action.payload.data || action.payload;
        const promoCodeData = data.promo_code || data;
        if (promoCodeData) {
          const index = state.promoCodes.findIndex(promo => promo.id === promoCodeData.id);
          if (index !== -1) {
            state.promoCodes[index] = promoCodeData;
          }
          if (state.promoCode && state.promoCode.id === promoCodeData.id) {
            state.promoCode = promoCodeData;
          }
        }
      })
      .addCase(updatePromoCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete promo code
      .addCase(deletePromoCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePromoCode.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'تم حذف البرومو كود بنجاح';
        state.promoCodes = state.promoCodes.filter(promo => promo.id !== action.payload);
        if (state.promoCode && state.promoCode.id === action.payload) {
          state.promoCode = null;
        }
      })
      .addCase(deletePromoCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearSuccess, clearPromoCode } = promoCodesSlice.actions;
export default promoCodesSlice.reducer;
